import { Injectable, inject } from '@angular/core';
import { Api } from '../api/api';
import {
  adminSuccessStoriesControllerListSuccessStoriesV1,
  adminSuccessStoriesControllerGetSuccessStoryV1,
  adminSuccessStoriesControllerCreateSuccessStoryV1,
  adminSuccessStoriesControllerUpdateSuccessStoryV1,
  adminSuccessStoriesControllerDeleteSuccessStoryV1,
  adminSuccessStoriesControllerReorderSuccessStoriesV1,
  adminSuccessStoriesControllerTogglePublishV1,
} from '../api/functions';
import type {
  SuccessStoryResponseAdminDto,
  CreateSuccessStoryDto,
  UpdateSuccessStoryDto,
} from '../api/models';

/**
 * Success Stories API Service
 * Wrapper around generated API functions for success stories management
 */
@Injectable({ providedIn: 'root' })
export class SuccessStoriesApiService {
  private readonly api = inject(Api);

  /**
   * Get all success stories (including unpublished)
   */
  async listSuccessStories(): Promise<SuccessStoryResponseAdminDto[]> {
    return this.api.invoke(adminSuccessStoriesControllerListSuccessStoriesV1, {});
  }

  /**
   * Get single success story by ID
   */
  async getSuccessStory(id: string): Promise<SuccessStoryResponseAdminDto> {
    return this.api.invoke(adminSuccessStoriesControllerGetSuccessStoryV1, { id });
  }

  /**
   * Create new success story with optional cover image
   */
  async createSuccessStory(
    data: CreateSuccessStoryDto,
    coverImage?: File
  ): Promise<SuccessStoryResponseAdminDto> {
    const body: any = {
      title: data.title,
      description: data.description,
      linkUrl: data.linkUrl,
    };

    if (data.order !== undefined) {
      body.order = data.order;
    }

    // Add cover image if provided
    if (coverImage) {
      body.coverImage = coverImage;
    }

    return this.api.invoke(adminSuccessStoriesControllerCreateSuccessStoryV1, {
      body: body,
    });
  }

  /**
   * Update success story with optional new cover image
   */
  async updateSuccessStory(
    id: string,
    data: UpdateSuccessStoryDto,
    coverImage?: File
  ): Promise<SuccessStoryResponseAdminDto> {
    const body: any = {};

    // Add text fields (only if provided)
    if (data.title !== undefined) {
      body.title = data.title;
    }
    if (data.description !== undefined) {
      body.description = data.description;
    }
    if (data.linkUrl !== undefined) {
      body.linkUrl = data.linkUrl;
    }
    if (data.order !== undefined) {
      body.order = data.order;
    }
    if (data.isPublished !== undefined) {
      body.isPublished = data.isPublished;
    }

    // Add cover image if provided
    if (coverImage) {
      body.coverImage = coverImage;
    }

    return this.api.invoke(adminSuccessStoriesControllerUpdateSuccessStoryV1, {
      id,
      body: body,
    });
  }

  /**
   * Delete success story
   */
  async deleteSuccessStory(id: string): Promise<void> {
    await this.api.invoke(adminSuccessStoriesControllerDeleteSuccessStoryV1, { id });
  }

  /**
   * Reorder success stories
   */
  async reorderSuccessStories(
    stories: Array<{ id: string; order: number }>
  ): Promise<SuccessStoryResponseAdminDto[]> {
    return this.api.invoke(adminSuccessStoriesControllerReorderSuccessStoriesV1, {
      body: { stories },
    });
  }

  /**
   * Toggle publish status
   */
  async togglePublish(id: string, isPublished: boolean): Promise<SuccessStoryResponseAdminDto> {
    return this.api.invoke(adminSuccessStoriesControllerTogglePublishV1, {
      id,
      body: { isPublished },
    });
  }

  /**
   * Get cover image URL for display
   */
  getCoverImageUrl(fileId: string): string {
    if (!fileId) return '';
    // Assuming the backend serves files at /api/v1/files/file/:id
    return `${this.api.rootUrl}/v1/files/file/${fileId}`;
  }
}
